import React from 'react';

import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import { getErrorsFromResponses, prepareResponsePayload } from '../forms/ResponderHelpers';
import { momentToTimestamp } from '../projects/projectHelpers';
import { compareStringArrays, getIdMap } from './helpers';

/**
 * Split out global codes and format data without splitting
 * into new/modified and keeping unmodified codes
 * @param {array} oldCostcodes from before editing
 * @param {array} newPhases all phases present after editing
 * @param {array} newCostcodes all costcodes present after editing
 * @returns {object} global costcodes, normal costcodes, and phases
 */
export const generateEditCostcodePayload = ({
  oldCostcodes,
  newPhases,
  newCostcodes,
}) => {
  const oldCostcodeMap = getIdMap(oldCostcodes);
  const globalCostcodes = [];
  const createdCostcodes = [];

  if (newCostcodes) {
    newCostcodes.forEach((costcode) => {
      if (costcode.isNew || costcode.id in oldCostcodeMap) {
        createdCostcodes.push({
          id: costcode.id,
          code: costcode.code,
          name: costcode.name,
          description: costcode.description,
          phased: costcode.phased,
          categoryIds: costcode.categoryIds,
          categoryAssignments: costcode.categoryAssignments,
          hourlyWage: costcode.hourlyWage,
          dailyWage: costcode.dailyWage,
          wageAdjustment: costcode.wageAdjustment,
          wageMultiplier: costcode.wageMultiplier,
          hourlyBillingRate: costcode.hourlyBillingRate,
          dailyBillingRate: costcode.dailyBillingRate,
          customData: costcode.customData,
          intuitId: costcode.intuitId,
        });
      } else {
        globalCostcodes.push({
          id: costcode.id,
          phased: costcode.phased,
          categoryIds: costcode.categoryIds,
          categoryAssignments: costcode.categoryAssignments,
          hourlyWage: costcode.hourlyWage,
          dailyWage: costcode.dailyWage,
          wageAdjustment: costcode.wageAdjustment,
          wageMultiplier: costcode.wageMultiplier,
          hourlyBillingRate: costcode.hourlyBillingRate,
          dailyBillingRate: costcode.dailyBillingRate,
        });
      }
    });
  }

  const createdPhases = [];
  if (newPhases) {
    newPhases.forEach((phase) => {
      createdPhases.push({
        name: phase.name,
        description: phase.description,
        estimatedLabourHours: phase.estimatedLabourHours,
        estimatedLabourCost: phase.estimatedLabourCost,
        estimatedOverheadHours: phase.estimatedOverheadHours,
        estimatedMaterialCost: phase.estimatedMaterialCost,
        estimatedOverheadCost: phase.estimatedOverheadCost,
        estimatedEquipmentHours: phase.estimatedEquipmentHours,
        estimatedEquipmentCost: phase.estimatedEquipmentCost,
        costcodes: phase.costcodes.map((cc) => ({
          id: cc.id,
          startDate: momentToTimestamp(cc.startDate),
          endDate: momentToTimestamp(cc.endDate),
          categoryIds: cc.categoryIds,
          categoryAssignments: cc.categoryAssignments,
        })),
      });
    });
  }

  return {
    globalCostcodes,
    createdCostcodes,
    createdPhases,
  };
};

// Split out global codes and format data
// without splitting into new/modified and keeping unmodified
/**
 * Adds all costcodes that belong to each phase into an array
 * @param {array} phases
 * @param {array} phaseCodes
 * @param {array} costcodes
 * @returns {array} phases with costcodes property added
 */
export const condenseTemplatePhasesAndCostcodes = (phases, phaseCodes, costcodes) => {
  const phaseMap = {};
  const costcodeMap = getIdMap(costcodes);
  phases.forEach((phase) => {
    const {
      id,
      name,
      description,
      estimatedLabourHours,
      estimatedLabourCost,
      estimatedOverheadHours,
      estimatedOverheadCost,
      estimatedEquipmentHours,
      estimatedEquipmentCost,
      estimatedMaterialCost,
    } = phase;
    phaseMap[id] = {
      id,
      name,
      description,
      estimatedLabourHours,
      estimatedLabourCost,
      estimatedOverheadHours,
      estimatedOverheadCost,
      estimatedEquipmentHours,
      estimatedEquipmentCost,
      estimatedMaterialCost,
      costcodes: [],
    };
  });
  phaseCodes.forEach((pc) => {
    const {
      phaseId,
      costcodeId,
      hours,
      days,
      estimatedCost,
      estimatedQuantity,
      units,
      startDate,
      endDate,
      categoryAssignments: phasedCategoryAssignments,
    } = pc;
    if (phaseMap[phaseId] && costcodeMap[costcodeId]) {
      const cc = costcodeMap[costcodeId];
      const {
        categoryId,
        categoryAssignments,
        categoryIds,
      } = cc;
      // HARBOUR-8131: backwards compatibility for existing job costing templates
      let categoryAssignmentsToUse = phasedCategoryAssignments ?? categoryAssignments;
      const categoryIdsToUse = categoryIds ?? [categoryId];
      if (!categoryAssignmentsToUse) {
        categoryAssignmentsToUse = {
          [categoryId]: {
            defaultCategoryId: categoryId,
            hours,
            days,
            estimatedCost,
            estimatedQuantity,
            units,
            phaseId,
            costcodeId,
          },
        };
      }
      phaseMap[phaseId].costcodes.push({
        ...cc,
        startDate,
        endDate,
        categoryAssignments: categoryAssignmentsToUse,
        categoryIds: categoryIdsToUse,
      });
    }
  });
  return Object.values(phaseMap);
};

export const prepareResponsesOnPayload = ({
  payload,
  responses,
  customFields,
}) => {
  const { responses: preparedResponses } = prepareResponsePayload({
    sections: customFields,
    responses,
  });

  return {
    ...payload,
    customData: preparedResponses,
  };
};

export const formatCustomFieldCreatePayload = ({
  payload = {},
  customFields,
}) => {
  const responses = payload.customData;

  const {
    errorMap,
  } = getErrorsFromResponses({ sections: customFields, responses });
  if (Object.keys(errorMap).length) {
    return {
      error: true,
      errorMap,
    };
  }

  return prepareResponsesOnPayload({
    payload,
    responses,
    customFields,
  });
};

// A template's costcode entry keeps the real global costcode's id for its
// entire lifetime (the backend never reassigns it once linked), so id is a
// reliable match key even though other snapshot fields like code/name can
// go stale between template edits.
export const getTemplatesUsingCostcode = ({ templates = [], costcodeId }) => (
  templates.filter((template) => (
    (template.data?.costcodes || []).some((cc) => cc.id === costcodeId)
  ))
);

const getTemplateSyncWarning = (templateCount) => {
  const plural = templateCount > 1;
  return `This cost code is used in ${templateCount} template${plural ? 's' : ''}. Saving these changes will also update ${plural ? 'those templates' : 'that template'}. Existing projects will not be affected.`;
};

const confirmCostcodeUpdateWarnings = (warnings) => new Promise((resolve) => {
  const content = warnings.length === 1
    ? (
      <div className="costcode-update-warning">
        {warnings[0]}
      </div>
    )
    : (
      <ul className="costcode-update-warning costcode-update-warning-list">
        {warnings.map((warning) => (
          <li key={warning}>{warning}</li>
        ))}
      </ul>
    );

  CustomConfirmModal({
    title: 'Warning',
    content,
    okText: 'Save',
    cancelText: 'Cancel',
    style: { width: 480 },
    onOk() {
      resolve(true);
    },
    onCancel() {
      resolve(false);
    },
  });
});

export const getCostcodeUpdatePayload = async ({
  id,
  payload,
  customFields,
  costcodes,
  templates = [],
}) => {
  const warningKeys = new Set([
    'hourlyWage',
    'dailyWage',
    'wageAdjustment',
    'wageMultiplier',
    'hourlyBillingRate',
    'dailyBillingRate',
  ]);

  const responses = payload.customData;

  const {
    errorMap,
  } = getErrorsFromResponses({ sections: customFields, responses });
  if (Object.keys(errorMap).length) {
    return {
      error: true,
      errorMap,
    };
  }

  const relevantCostcode = costcodes.find((cc) => cc.id === id);

  const newPayload = { ...payload };

  const {
    categoryIds: newCategoryIds,
  } = payload;

  if (newCategoryIds) {
    const {
      categoryIds: oldCategoryIds,
    } = relevantCostcode;

    const {
      added: addedCategoryIds,
      removed: removedCategoryIds,
    } = compareStringArrays(oldCategoryIds, newCategoryIds);

    newPayload.addedCategoryIds = addedCategoryIds;
    newPayload.removedCategoryIds = removedCategoryIds;
    delete newPayload.categoryIds;
  }

  const warnings = [];

  // Only display warning if wage history is empty
  // and one of the warning keys is being updated
  if (
    !relevantCostcode?.wageHistory?.length
    && Object.keys(newPayload).some((key) => warningKeys.has(key))
  ) {
    warnings.push('Are you sure you want to edit this cost code? This will update all existing tasks with the new wage and billing rates.');
  }

  const affectedTemplates = getTemplatesUsingCostcode({ templates, costcodeId: id });
  if (affectedTemplates.length > 0) {
    warnings.push(getTemplateSyncWarning(affectedTemplates.length));
  }

  const preparedPayload = prepareResponsesOnPayload({
    payload: newPayload,
    responses,
    customFields,
  });

  if (!warnings.length) return preparedPayload;

  const confirmed = await confirmCostcodeUpdateWarnings(warnings);
  if (!confirmed) return undefined;
  return preparedPayload;
};

export const getCategoryAssignmentValue = (costcode, categoryId, key) => {
  const {
    categoryAssignments: {
      [categoryId]: {
        [key]: value,
      } = {},
    } = {},
  } = costcode ?? {};
  return value;
};

export const LABOR_CATEGORY_ID = 1;
export const MATERIAL_CATEGORY_ID = 2;
export const OVERHEAD_CATEGORY_ID = 3;
export const EQUIPMENT_CATEGORY_ID = 4;

export const ID_TO_CATEGORY_MAP = {
  [LABOR_CATEGORY_ID]: 'Labor',
  [MATERIAL_CATEGORY_ID]: 'Material',
  [OVERHEAD_CATEGORY_ID]: 'Overhead',
  [EQUIPMENT_CATEGORY_ID]: 'Equipment',
};

export const getQuantityTrackingCategoryIds = (categoriesIdMap = {}) => {
  const categoryIds = Object.values(categoriesIdMap)
    .filter((category) => category?.allowQuantityTracking)
    .map((category) => category.id);

  if (categoryIds.length) return categoryIds;

  return [MATERIAL_CATEGORY_ID];
};

export const costcodeHasQuantityTrackingCategory = (costcode, quantityTrackingCategoryIdSet) => (
  costcode?.categoryIds?.some((categoryId) => quantityTrackingCategoryIdSet.has(categoryId))
);

export const getNewCategoryAssignments = ({
  oldCostcode,
  newData,
}) => {
  const {
    categoryAssignments: existingCategoryAssignments = {},
  } = oldCostcode ?? {};
  const {
    categoryAssignments: updatedCategoryAssignments = {},
  } = newData ?? {};

  const newCategoryAssignmentsObj = { ...existingCategoryAssignments };
  Object.keys(updatedCategoryAssignments).forEach((categoryId) => {
    newCategoryAssignmentsObj[categoryId] = {
      ...existingCategoryAssignments?.[categoryId],
      ...updatedCategoryAssignments?.[categoryId],
    };
  });
  return newCategoryAssignmentsObj;
};

/**
 * Splits a unified categoryId into default/custom column values.
 * Mirrors backend getDefaultCategoryIds.
 */
export const splitCategoryId = (categoryId) => {
  const isCustom = typeof categoryId === 'string' && categoryId?.length > 1;

  return {
    defaultCategoryId: isCustom ? null : Number(categoryId),
    customCategoryId: isCustom ? categoryId : null,
  };
};

const filterQuantityTrackingCategories = (categoryIds, categoriesIdMap) => {
  const allowed = new Set(getQuantityTrackingCategoryIds(categoriesIdMap));
  return categoryIds.filter((id) => allowed.has(id));
};

const filterEquipmentAndLaborCategories = (categoryIds) => (
  categoryIds.filter((id) => id === EQUIPMENT_CATEGORY_ID || id === LABOR_CATEGORY_ID)
);

/**
 * Returns eligible category IDs for a costcode, optionally filtered by entry type.
 * @param {object} costcode
 * @param {object} categoriesIdMap
 * @param {object} options
 * @param {'quantityTracking'|'equipmentAndLabor'|function} [options.filter]
 */
export const getEligibleCategoriesForCostcode = (
  costcode,
  categoriesIdMap = {},
  { filter, allowedCategoryIds } = {},
) => {
  const { categoryIds = [] } = costcode ?? {};
  if (!categoryIds.length) return [];

  let result;
  if (typeof filter === 'function') {
    result = filter(categoryIds, categoriesIdMap, costcode);
  } else if (filter === 'quantityTracking') {
    result = filterQuantityTrackingCategories(categoryIds, categoriesIdMap);
  } else if (filter === 'equipmentAndLabor') {
    result = filterEquipmentAndLaborCategories(categoryIds);
  } else {
    result = categoryIds;
  }

  if (allowedCategoryIds?.length) {
    const allowed = new Set(allowedCategoryIds.map(String));
    result = result.filter((id) => allowed.has(String(id)));
  }

  return result;
};

export const getCostcodeColumnAllowedCategoryIds = (columns = [], columnKey = 'costcode') => {
  const column = columns.find(({ key }) => key === columnKey);
  const { allowedCostCodeCategoryIds } = column ?? {};
  return allowedCostCodeCategoryIds?.length ? allowedCostCodeCategoryIds : undefined;
};

/** Category options for Material/Equipment Cost Code column gear settings. */
export const getTableCostCodeCategorySettingOptions = (dataType, categoriesIdMap = {}) => {
  if (dataType === 'Materials') {
    return getQuantityTrackingCategoryIds(categoriesIdMap).map((categoryId) => ({
      value: categoryId,
      label: categoriesIdMap[categoryId]?.name ?? ID_TO_CATEGORY_MAP[categoryId] ?? String(categoryId),
    }));
  }
  if (dataType === 'Equipment') {
    return [LABOR_CATEGORY_ID, EQUIPMENT_CATEGORY_ID].map((categoryId) => ({
      value: categoryId,
      label: categoriesIdMap[categoryId]?.name ?? ID_TO_CATEGORY_MAP[categoryId] ?? String(categoryId),
    }));
  }
  return [];
};

export const getCategoryFieldsFromId = (categoryId, categoriesIdMap = {}) => {
  if (!categoryId) {
    return {
      categoryId: null,
      defaultCategoryId: null,
      customCategoryId: null,
      categoryName: null,
    };
  }
  const { defaultCategoryId, customCategoryId } = splitCategoryId(categoryId);
  return {
    categoryId,
    defaultCategoryId,
    customCategoryId,
    categoryName: categoriesIdMap[categoryId]?.name || null,
  };
};

export const getAutoCategoryFields = (
  costcode,
  filter,
  allowedCategoryIds,
  categoriesIdMap = {},
) => {
  const eligibleIds = getEligibleCategoriesForCostcode(costcode, categoriesIdMap, {
    filter,
    allowedCategoryIds,
  });
  if (eligibleIds.length !== 1) return null;
  return getCategoryFieldsFromId(eligibleIds[0], categoriesIdMap);
};

/**
 * Category name to show next to a costcode label.
 * Hidden when there is at most one eligible category (nothing to disambiguate).
 */
const getCostcodeLabelCategoryName = (
  costcode,
  categoriesIdMap,
  { filter, categoryName, allowedCategoryIds } = {},
) => {
  if (!categoryName) return null;
  const eligibleIds = getEligibleCategoriesForCostcode(
    costcode,
    categoriesIdMap,
    { filter, allowedCategoryIds },
  );
  return eligibleIds.length > 1 ? categoryName : null;
};

/** Parses TreeSelect values: `phaseId.costcodeId` or `phaseId.costcodeId.categoryId`. */
export const parsePhaseCostcodeCategoryKey = (selectedVal = '') => {
  const parts = selectedVal.split('.');
  if (parts.length < 2) {
    return { phaseId: null, costcodeId: null, categoryId: null };
  }

  return {
    phaseId: parts[0],
    costcodeId: parts[1],
    categoryId: parts[2] ?? null,
  };
};

const EMPTY_COSTCODE_FIELDS = {
  phaseId: null,
  phaseName: null,
  costcodeId: null,
  costcode: '',
  phaseCostcodeKey: '',
  categoryId: null,
  defaultCategoryId: null,
  customCategoryId: null,
  categoryName: null,
};

/** Display label for material/equipment table rows (name only, no phase/code). */
export const formatFormTableCostcodeName = ({ name, categoryName }) => (
  categoryName ? `${name} (${categoryName})` : name
);

const getPreferredFormTableCategoryId = (categoryFilter, eligibleIds) => {
  let preferredId;
  if (categoryFilter === 'quantityTracking') {
    preferredId = MATERIAL_CATEGORY_ID;
  } else if (categoryFilter === 'equipmentAndLabor') {
    preferredId = EQUIPMENT_CATEGORY_ID;
  }
  return eligibleIds.find((id) => String(id) === String(preferredId)) ?? eligibleIds[0];
};

/** Build form-table costcode fields from stored costcodeId + optional phaseId. */
export const getNestedTableCostcodeFromLabourSelection = ({
  labourCostcodeKey,
  labourPhaseId,
  costcodeMap = {},
  categoriesIdMap = {},
  categoryFilter,
  allowedCategoryIds,
}) => {
  if (!allowedCategoryIds?.length || !labourCostcodeKey) return null;

  const parsed = String(labourCostcodeKey).includes('.')
    ? parsePhaseCostcodeCategoryKey(labourCostcodeKey)
    : {
      phaseId: labourPhaseId,
      costcodeId: labourCostcodeKey,
      categoryId: null,
    };

  const { costcodeId, phaseId: parsedPhaseId } = parsed;
  const costcodeObj = costcodeMap[costcodeId];
  if (!costcodeObj?.categoryIds?.length) return null;

  const eligibleIds = getEligibleCategoriesForCostcode(costcodeObj, categoriesIdMap, {
    filter: categoryFilter,
    allowedCategoryIds,
  });
  if (!eligibleIds.length) return null;

  let phasePart = parsedPhaseId || labourPhaseId || 'unphased';
  // Time-entry TreeSelect uses `global.*`; material/equipment tables use `unphased.*`.
  if (phasePart === 'global' || phasePart === 'Unphased') {
    phasePart = 'unphased';
  }

  return getFormTableCostcodeFieldsFromIds({
    costcodeId,
    phaseId: phasePart,
    costcodeMap,
    categoriesIdMap,
    categoryFilter,
    allowedCategoryIds,
  });
};

export const getFormTableCostcodeFieldsFromIds = ({
  costcodeId,
  phaseId,
  costcodeMap = {},
  categoriesIdMap = {},
  categoryFilter,
  allowedCategoryIds,
}) => {
  // If the costcode is not found, return empty fields
  if (!costcodeId || !costcodeMap[costcodeId]) return { ...EMPTY_COSTCODE_FIELDS };
  const costcodeObj = costcodeMap[costcodeId];
  const phasePart = phaseId || 'unphased';
  const eligibleIds = categoryFilter
    ? getEligibleCategoriesForCostcode(costcodeObj, categoriesIdMap, {
      filter: categoryFilter,
      allowedCategoryIds,
    })
    : [];

  if (categoryFilter && !eligibleIds.length) {
    return { ...EMPTY_COSTCODE_FIELDS };
  }

  let categoryFields = {
    categoryId: null,
    defaultCategoryId: null,
    customCategoryId: null,
    categoryName: null,
  };
  let categorySuffix = '';

  const autoCategory = categoryFilter
    ? getAutoCategoryFields(costcodeObj, categoryFilter, allowedCategoryIds, categoriesIdMap)
    : null;
  if (autoCategory) {
    categoryFields = autoCategory;
  } else if (eligibleIds.length > 1) {
    const selectedCategoryId = getPreferredFormTableCategoryId(categoryFilter, eligibleIds);
    categoryFields = getCategoryFieldsFromId(selectedCategoryId, categoriesIdMap);
    categorySuffix = `.${selectedCategoryId}`;
  }

  return {
    ...EMPTY_COSTCODE_FIELDS,
    ...categoryFields,
    costcodeId,
    phaseId: phasePart,
    costcode: formatFormTableCostcodeName({
      name: costcodeObj.name,
      categoryName: getCostcodeLabelCategoryName(
        costcodeObj,
        categoriesIdMap,
        {
          filter: categoryFilter,
          categoryName: categoryFields.categoryName,
          allowedCategoryIds,
        },
      ),
    }),
    phaseCostcodeKey: `${phasePart}.${costcodeId}${categorySuffix}`,
  };
};

export const getFormTableCostcodeFieldsWithLabourOverride = ({
  costcodeId,
  phaseId,
  costcodeMap,
  categoriesIdMap,
  categoryFilter,
  allowedCategoryIds,
  labourMappedCostcodeFields,
}) => {
  if (allowedCategoryIds?.length && labourMappedCostcodeFields?.costcodeId) {
    return labourMappedCostcodeFields;
  }
  return getFormTableCostcodeFieldsFromIds({
    costcodeId,
    phaseId,
    costcodeMap,
    categoriesIdMap,
    categoryFilter,
    allowedCategoryIds,
  });
};

/** TreeSelect value for rows that may predate phaseCostcodeKey storage. */
export const getFormTableCostcodeSelectValue = (record = {}, { costcodeMap } = {}) => {
  if (!record.costcodeId) return undefined;
  if (costcodeMap && !costcodeMap[record.costcodeId]) return undefined;
  if (record.phaseCostcodeKey) return record.phaseCostcodeKey;

  const phasePart = record.phaseId && record.phaseId !== 'unphased' ? record.phaseId : 'unphased';
  const base = `${phasePart}.${record.costcodeId}`;
  return record.categoryId ? `${base}.${record.categoryId}` : base;
};

export const getFormTableCostcodeUpdateFields = ({
  selectedVal,
  phaseMap = {},
  costcodeMap = {},
  categoriesIdMap = {},
  categoryFilter,
  allowedCategoryIds,
  formatCostcode,
}) => {
  if (!selectedVal) return { ...EMPTY_COSTCODE_FIELDS };

  const { phaseId, costcodeId, categoryId: selectedCategoryId } = (
    parsePhaseCostcodeCategoryKey(selectedVal)
  );
  const { name: phaseName = 'Unphased' } = phaseMap[phaseId] || {};
  const costcodeObj = costcodeMap[costcodeId] || {};
  const { name: costcodeName, code } = costcodeObj;

  let categoryFields = {
    categoryId: null,
    defaultCategoryId: null,
    customCategoryId: null,
    categoryName: null,
  };

  if (selectedCategoryId) {
    categoryFields = getCategoryFieldsFromId(selectedCategoryId, categoriesIdMap);
  } else {
    const autoCategory = getAutoCategoryFields(
      costcodeObj,
      categoryFilter,
      allowedCategoryIds,
      categoriesIdMap,
    );
    if (autoCategory) categoryFields = autoCategory;
  }

  const costcode = formatCostcode({
    phase: phaseName,
    code,
    name: costcodeName,
    categoryName: getCostcodeLabelCategoryName(
      costcodeObj,
      categoriesIdMap,
      {
        filter: categoryFilter,
        categoryName: categoryFields.categoryName,
        allowedCategoryIds,
      },
    ),
    costcodeObj,
  });

  return {
    phaseId,
    phaseName,
    costcodeId,
    costcode,
    phaseCostcodeKey: selectedVal,
    ...categoryFields,
  };
};
